import { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useBIFields } from '../hooks/useBIFields';
import type { FilterCondition } from '../../../types/bi';
import styles from './FilterBar.module.css';

const TEXT_OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: '=', label: '=' },
  { value: '!=', label: '≠' },
  { value: 'in', label: 'in' },
  { value: 'not_in', label: 'not in' },
  { value: 'like', label: 'contains' },
  { value: 'is_null', label: 'is null' },
  { value: 'is_not_null', label: 'is not null' },
];

const NUMERIC_DATE_OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: '=', label: '=' },
  { value: '!=', label: '≠' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '≥' },
  { value: '<=', label: '≤' },
  { value: 'in', label: 'in' },
  { value: 'not_in', label: 'not in' },
  { value: 'between', label: 'between' },
  { value: 'is_null', label: 'is null' },
  { value: 'is_not_null', label: 'is not null' },
];

const OPTION_OPERATORS: { value: FilterCondition['operator']; label: string }[] = [
  { value: '=', label: '=' },
  { value: '!=', label: '≠' },
  { value: 'is_null', label: 'is null' },
  { value: 'is_not_null', label: 'is not null' },
];

type FilterValueKind = 'text' | 'number' | 'date' | 'option';

const NUMERIC_TYPES = new Set([
  'number',
  'numeric',
  'integer',
  'float',
  'decimal',
  'currency',
  'percentage',
]);
const DATE_TYPES = new Set(['date', 'datetime', 'timestamp', 'timestamptz']);
const OPTION_TYPES = new Set(['dropdown', 'multiple', 'select', 'multi_select']);

function resolveFilterValueKind(fieldType?: string, dbType?: string): FilterValueKind {
  const field = (fieldType || '').toLowerCase();
  const db = (dbType || '').toLowerCase();

  if (OPTION_TYPES.has(field) || OPTION_TYPES.has(db)) return 'option';
  if (NUMERIC_TYPES.has(field) || NUMERIC_TYPES.has(db)) return 'number';
  if (DATE_TYPES.has(field) || DATE_TYPES.has(db)) return 'date';
  return 'text';
}

function getOperatorsForKind(kind: FilterValueKind) {
  if (kind === 'number' || kind === 'date') return NUMERIC_DATE_OPERATORS;
  if (kind === 'option') return OPTION_OPERATORS;
  return TEXT_OPERATORS;
}

function parseScalarFilterValue(raw: string, kind: FilterValueKind): unknown {
  if (kind === 'number') {
    if (!raw.trim()) return '';
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : '';
  }

  return raw;
}

function normaliseFilterValue(
  operator: FilterCondition['operator'],
  currentValue: unknown,
  kind: FilterValueKind = 'text',
) {
  if (operator === 'between') {
    if (Array.isArray(currentValue)) {
      return [
        parseScalarFilterValue(String(currentValue[0] ?? ''), kind),
        parseScalarFilterValue(String(currentValue[1] ?? ''), kind),
      ];
    }

    return ['', ''];
  }

  if (operator === 'in' || operator === 'not_in') {
    if (Array.isArray(currentValue)) {
      return currentValue
        .map((value) => parseScalarFilterValue(String(value ?? ''), kind))
        .filter((value) => value !== '');
    }

    const text = String(currentValue ?? '').trim();
    return text
      ? text
          .split(',')
          .map((value) => parseScalarFilterValue(value.trim(), kind))
          .filter((value) => value !== '')
      : [];
  }

  if (Array.isArray(currentValue)) {
    return parseScalarFilterValue(String(currentValue[0] ?? ''), kind);
  }

  return parseScalarFilterValue(String(currentValue ?? ''), kind);
}

function formatDelimitedValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry ?? '')).join(', ');
  }

  return String(value ?? '');
}

interface FilterBarProps {
  filters: FilterCondition[];
  onChange: (filters: FilterCondition[]) => void;
}

export default function FilterBar({ filters, onChange }: FilterBarProps) {
  const { data: fieldsData } = useBIFields();
  const [expanded, setExpanded] = useState(filters.length > 0);
  const [fieldSearchValues, setFieldSearchValues] = useState<Record<number, string>>({});

  const allFields =
    fieldsData?.categories.flatMap((c) => c.fields.map((f) => ({ ...f, category: c.name }))) || [];

  const add = useCallback(() => {
    onChange([...filters, { field_key: '', operator: '=', value: '' }]);
    setExpanded(true);
  }, [filters, onChange]);

  const update = useCallback(
    (idx: number, patch: Partial<FilterCondition>) => {
      onChange(filters.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
    },
    [filters, onChange],
  );

  const remove = useCallback(
    (idx: number) => {
      const next = filters.filter((_, i) => i !== idx);
      onChange(next);
      if (next.length === 0) setExpanded(false);
    },
    [filters, onChange],
  );

  return (
    <div className={styles.bar}>
      <button
        className={styles.toggleBtn}
        onClick={() => (filters.length > 0 ? setExpanded(!expanded) : add())}
      >
        <FontAwesomeIcon icon={faFilter} />
        <span>Filters{filters.length > 0 ? ` (${filters.length})` : ''}</span>
      </button>

      {expanded && (
        <div className={styles.filterList}>
          {filters.map((f, i) => (
            <div key={i} className={styles.row}>
              {(() => {
                const fieldDef = allFields.find((fd) => fd.field_key === f.field_key);
                const valueKind = resolveFilterValueKind(fieldDef?.field_type, fieldDef?.db_type);
                const operatorOptions = getOperatorsForKind(valueKind);
                const selectedFieldLabel = fieldDef
                  ? `${fieldDef.field_name} (${fieldDef.category})`
                  : '';
                const fieldSearchValue =
                  fieldSearchValues[i] !== undefined ? fieldSearchValues[i] : selectedFieldLabel;
                const optionValues = fieldDef?.options || [];
                const valueInputType =
                  f.operator === 'like'
                    ? 'text'
                    : valueKind === 'number'
                      ? 'number'
                      : valueKind === 'date'
                        ? 'date'
                        : 'text';

                return (
                  <>
                    <>
                      <input
                        className={styles.select}
                        list={`global-filter-fields-${i}`}
                        aria-label={`global-filter-field-${i}`}
                        placeholder="— field —"
                        value={fieldSearchValue}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setFieldSearchValues((prev) => ({ ...prev, [i]: nextValue }));

                          const raw = nextValue.trim().toLowerCase();
                          if (!raw) {
                            update(i, { field_key: '' });
                            return;
                          }

                          const match = allFields.find(
                            (fd) =>
                              fd.field_key.toLowerCase() === raw ||
                              fd.field_name.toLowerCase() === raw ||
                              `${fd.field_name} (${fd.category})`.toLowerCase() === raw,
                          );
                          if (!match) return;

                          const nextKind = resolveFilterValueKind(match.field_type, match.db_type);
                          const nextOps = getOperatorsForKind(nextKind);
                          const nextOperator = nextOps.some((op) => op.value === f.operator)
                            ? f.operator
                            : nextOps[0].value;

                          update(i, {
                            field_key: match.field_key,
                            operator: nextOperator,
                            value: normaliseFilterValue(nextOperator, f.value, nextKind),
                          });
                        }}
                        onBlur={(e) => {
                          const raw = e.target.value.trim().toLowerCase();
                          const match = allFields.find(
                            (fd) =>
                              fd.field_key.toLowerCase() === raw ||
                              fd.field_name.toLowerCase() === raw ||
                              `${fd.field_name} (${fd.category})`.toLowerCase() === raw,
                          );
                          setFieldSearchValues((prev) => ({
                            ...prev,
                            [i]: match
                              ? `${match.field_name} (${match.category})`
                              : selectedFieldLabel,
                          }));
                        }}
                      />
                      <datalist id={`global-filter-fields-${i}`}>
                        {allFields.map((fd) => (
                          <option key={fd.field_key} value={`${fd.field_name} (${fd.category})`} />
                        ))}
                      </datalist>
                    </>
                    <select
                      className={styles.selectOp}
                      value={f.operator}
                      onChange={(e) =>
                        update(i, {
                          operator: e.target.value as FilterCondition['operator'],
                          value: normaliseFilterValue(
                            e.target.value as FilterCondition['operator'],
                            f.value,
                            valueKind,
                          ),
                        })
                      }
                    >
                      {operatorOptions.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                    {valueKind === 'option' &&
                    f.operator !== 'is_null' &&
                    f.operator !== 'is_not_null' ? (
                      <select
                        className={styles.input}
                        value={String(f.value ?? '')}
                        onChange={(e) =>
                          update(i, {
                            value: e.target.value,
                          })
                        }
                      >
                        <option value="">— select option —</option>
                        {optionValues.map((opt) => (
                          <option key={`${opt.value}-${opt.label}`} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : f.operator === 'between' ? (
                      <div style={{ display: 'flex', gap: '0.25rem', flex: 1, minWidth: 0 }}>
                        <input
                          type={valueInputType}
                          className={styles.input}
                          placeholder="from"
                          value={Array.isArray(f.value) ? String(f.value[0] ?? '') : ''}
                          onChange={(e) =>
                            update(i, {
                              value: [
                                parseScalarFilterValue(e.target.value, valueKind),
                                Array.isArray(f.value)
                                  ? parseScalarFilterValue(String(f.value[1] ?? ''), valueKind)
                                  : '',
                              ],
                            })
                          }
                        />
                        <input
                          type={valueInputType}
                          className={styles.input}
                          placeholder="to"
                          value={Array.isArray(f.value) ? String(f.value[1] ?? '') : ''}
                          onChange={(e) =>
                            update(i, {
                              value: [
                                Array.isArray(f.value)
                                  ? parseScalarFilterValue(String(f.value[0] ?? ''), valueKind)
                                  : '',
                                parseScalarFilterValue(e.target.value, valueKind),
                              ],
                            })
                          }
                        />
                      </div>
                    ) : f.operator === 'in' || f.operator === 'not_in' ? (
                      <input
                        type="text"
                        className={styles.input}
                        placeholder={
                          valueKind === 'date' ? 'YYYY-MM-DD, YYYY-MM-DD' : 'value1, value2'
                        }
                        value={formatDelimitedValue(f.value)}
                        onChange={(e) =>
                          update(i, {
                            value: e.target.value
                              .split(',')
                              .map((value) => parseScalarFilterValue(value.trim(), valueKind))
                              .filter((value) => value !== ''),
                          })
                        }
                      />
                    ) : f.operator !== 'is_null' && f.operator !== 'is_not_null' ? (
                      <input
                        type={valueInputType}
                        className={styles.input}
                        placeholder={valueKind === 'date' ? 'YYYY-MM-DD' : 'value'}
                        value={String(f.value ?? '')}
                        onChange={(e) =>
                          update(i, { value: parseScalarFilterValue(e.target.value, valueKind) })
                        }
                      />
                    ) : null}
                    <button className={styles.removeBtn} onClick={() => remove(i)}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </>
                );
              })()}
            </div>
          ))}
          <button className={styles.addBtn} onClick={add}>
            <FontAwesomeIcon icon={faPlus} /> Add filter
          </button>
        </div>
      )}
    </div>
  );
}
